import { FormControlLabel } from '@material-ui/core'
import CustomField from 'elements/field/CustomField'
import ProUXButton from 'elements/proUXButtons/ProUXButton'
import InfoTooltip from 'elements/tooltip/InfoTooltip'
import { Radio, RadioGroup, styled, Switch, Typography } from 'opensolar-ui'
import { ChangeEvent, default as React, useEffect, useState } from 'react'
import { TextInput, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { doNotTranslate } from 'util/misc'
import { authSelectors } from '../../ducks/auth'
import PandadocCATemplateDialog from './PandadocCATemplateDialog'

type PropTypes = {
  disabled: boolean
}

const PandadocSettingsSwitchWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 15,
})

const PandadocSettingsSwitchContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column'
})

const StyledSpan = styled('span')({
  display: 'flex',
  alignItems: 'center',
})

export const PandadocSettingsSection: React.FunctionComponent<PropTypes> = (props) => {
  const form = useForm()

  const org = useSelector(authSelectors.getCurrentOrg)

  const isUSABased = org?.country?.iso2 === 'US'

  const getInitialCountersignType = () => {
    let record = form.getState().values
    if (!record) return undefined
    if (record.pandadoc_require_sales_rep_countersignature) return 'dynamic'
    else if (record.pandadoc_designated_countersigner_email) return 'fixed'
    else return undefined
  }

  type Countersign = undefined | 'fixed' | 'dynamic'

  const getCountersigningEnabled = () => getInitialCountersignType() !== undefined

  const [countersignEnabled, setCountersignEnabled] = useState<boolean>(getCountersigningEnabled())
  const [countersignType, setCountersignType] = useState<Countersign>(getInitialCountersignType())

  const [showCATemplateDialog, setShowCATemplateDialog] = useState<boolean>(false)
  const [separateDocumentsEnabled, setSeparateDocumentsEnabled] = useState<boolean>(
    form.getState().values?.pandadoc_return_as_separate_documents ?? false
  )

  useEffect(() => {
    if (form.getState().values.pandadoc_require_sales_rep_countersignature) {
      setCountersignType('dynamic')
    } else if (countersignType) {
      setCountersignType('fixed')
    }
  }, [form.getState().values, countersignType])

  useEffect(() => {
    form.change('enable_pandadoc_countersignature', countersignEnabled)
    if (!countersignEnabled) {
      form.change('pandadoc_require_sales_rep_countersignature', false)
      form.change('pandadoc_designated_countersigner_email', '')
      form.change('pandadoc_designated_countersigner_first_name', '')
      form.change('pandadoc_designated_countersigner_family_name', '')
    }
  }, [countersignEnabled])

  const translate = useTranslate()

  const handleCountersignToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setCountersignEnabled(e.target.checked)
  }

  const handleCountersignTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value: Countersign = e.target.value as Countersign
    setCountersignType(value)
    if (value === 'dynamic') form.change('pandadoc_require_sales_rep_countersignature', true)
    else form.change('pandadoc_require_sales_rep_countersignature', false)
  }

  const handleSeparateDocumentsToggle = (e: ChangeEvent<HTMLInputElement>) => {
    setSeparateDocumentsEnabled(e.target.checked)
    form.change('pandadoc_return_as_separate_documents', e.target.checked)
  }

  return (
    <PandadocSettingsSwitchWrapper>
      <PandadocSettingsSwitchContainer>
        <FormControlLabel
          label={translate('Enable Pandadoc Countersigner')}
          labelPlacement="start"
          control={<Switch checked={countersignEnabled} onChange={handleCountersignToggle} disabled={props.disabled} />}
        />

        <div style={{ padding: '10px', width: '100%', display: countersignEnabled ? undefined : 'none' }}>
          <Typography textVariant="body1Bold" style={{ textAlign: 'left', marginTop: '20px' }}>
            {translate('How would you like to choose the countersigner for this contract?')}
          </Typography>
          <RadioGroup
            onChange={handleCountersignTypeChange}
            style={{ display: 'flex', flexDirection: 'row' }}
            value={countersignType}
          >
            <FormControlLabel
              label={translate('Dynamic - Assigned Team Member is countersigner')}
              value="dynamic"
              control={<Radio />}
            />
            <FormControlLabel
              label={translate('Fixed - set one individual to be countersigner for all projects')}
              value="fixed"
              control={<Radio />}
            />
          </RadioGroup>
          {countersignType && (
            <>
              <p className="small">
                {countersignType === 'dynamic'
                  ? translate(
                      'The information below will enable a countersignature in the unlikely event that the Assigned Team Member cannot be used as the countersigner (e.g. that user does not have a first and last name entered in OpenSolar).'
                    )
                  : translate('The information below will be used to set the countersigner for every contract.')}
              </p>
              <CustomField
                component={TextInput}
                fullWidth={true}
                name="pandadoc_designated_countersigner_email"
                source="pandadoc_designated_countersigner_email"
                label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner Email`}
                disabled={props.disabled}
              />
              <CustomField
                component={TextInput}
                fullWidth={true}
                name="pandadoc_designated_countersigner_first_name"
                source="pandadoc_designated_countersigner_first_name"
                label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner First Name`}
                disabled={props.disabled}
              />
              <CustomField
                component={TextInput}
                fullWidth={true}
                name="pandadoc_designated_countersigner_family_name"
                source="pandadoc_designated_countersigner_family_name"
                label={`${countersignType === 'dynamic' ? 'Backup ' : 'Designated '}Countersigner Last Name`}
                disabled={props.disabled}
              />
            </>
          )}
          {isUSABased && (
            <div
              style={{
                margin: '20px 0px 50px 0px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <h4>California Consumer Protection Guide Template</h4>
              <p>
                {org?.pandadoc_config?.enable_california_protection_guide
                  ? 'Click the button below to edit or disable your existing California Consumer Protection Guide Pandadoc template.'
                  : 'Click the button below to automatically include the California Consumer Protection Guide as part of all Pandadoc contracts for projects based in California.'}
              </p>
              <ProUXButton
                type="primary"
                label={org?.pandadoc_config?.enable_california_protection_guide ? 'Edit Template' : 'Enable Template'}
                onClick={() => setShowCATemplateDialog(true)}
              />
              <StyledSpan>
                <FormControlLabel
                  disabled={!org?.pandadoc_config?.enable_california_protection_guide}
                  label={doNotTranslate('Return as Separate Documents')}
                  labelPlacement="end"
                  control={<Switch checked={separateDocumentsEnabled} onChange={handleSeparateDocumentsToggle} />}
                />
                <InfoTooltip
                  title={doNotTranslate(
                    'Toggling this setting on will return the signed Consumer Protection Guide, Supporting Info and Contract documents individually. Toggle off to return one merged document.'
                  )}
                />
              </StyledSpan>
              <PandadocCATemplateDialog isOpen={showCATemplateDialog} onClose={() => setShowCATemplateDialog(false)} />
            </div>
          )}
        </div>
      </PandadocSettingsSwitchContainer>
    </PandadocSettingsSwitchWrapper>
  )
}
export default PandadocSettingsSection
